import React from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  fetchActivityLogsRequest,
  fetchActivitySummaryRequest,
  setActivityLogsFilters,
  resetActivityLogsFilters,
} from "../../../dux/activityLog";
import { fetchUsersRequest, selectUsers } from "../../../dux/settings";
import styles from "./ActivityLog.module.css";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const sportMappingLabels = {
  "cricket-net": "Cricket Net",
  football: "Football",
  "box-cricket": "Box Cricket",
  "pickle-ball": "Pickle Ball",
};

const activityTypeLabels = {
  // Booking
  booking_created: "Booking Created",
  booking_updated: "Booking Updated",
  booking_cancelled: "Booking Cancelled",
  booking_completed: "Booking Completed",
  booking_payment_received: "Booking Payment Received",
  booking_payment_failed: "Booking Payment Failed",
  booking_status_changed: "Booking Status Changed",
  booking_amount_edited: "Booking Amount Edited",
  booking_equipment_added: "Booking Equipment Added",
  booking_equipment_removed: "Booking Equipment Removed",

  // Slot
  slot_blocked: "Slot Blocked",
  slot_unblocked: "Slot Unblocked",
  slot_available: "Slot Available",
  slot_updated: "Slot Updated",
  slot_created: "Slot Created",
  slot_deleted: "Slot Deleted",
  slot_batch_updated: "Slot Batch Updated",

  // User
  user_login: "User Login",
  user_logout: "User Logout",
  user_registered: "User Registered",
  user_profile_updated: "User Profile Updated",
  user_created: "User Created",
  user_updated: "User Updated",
  user_deleted: "User Deleted",
  user_activated: "User Activated",
  user_deactivated: "User Deactivated",
  user_roles_assigned: "User Roles Assigned",
  user_locations_assigned: "User Locations Assigned",
  user_password_changed: "User Password Changed",
  user_password_reset: "User Password Reset",
  user_feedback_submitted: "User Feedback Submitted",
  user_search_performed: "User Search Performed",
  user_list_retrieved: "User List Retrieved",

  // Pricing
  pricing_updated: "Pricing Updated",
  pricing_created: "Pricing Created",
  pricing_deleted: "Pricing Deleted",
  pricing_batch_updated: "Pricing Batch Updated",
  pricing_rule_added: "Pricing Rule Added",
  pricing_rule_removed: "Pricing Rule Removed",

  // Holiday
  holiday_added: "Holiday Added",
  holiday_removed: "Holiday Removed",
  holiday_updated: "Holiday Updated",
  holiday_batch_imported: "Holiday Batch Imported",

  // Special Schedule
  special_schedule_added: "Special Schedule Added",
  special_schedule_removed: "Special Schedule Removed",
  special_schedule_updated: "Special Schedule Updated",

  // Invoice
  invoice_generated: "Invoice Generated",
  invoice_updated: "Invoice Updated",
  invoice_paid: "Invoice Paid",
  invoice_cancelled: "Invoice Cancelled",
  invoice_deleted: "Invoice Deleted",
  invoice_sent: "Invoice Sent",
  invoice_overdue: "Invoice Overdue",

  // Payment
  payment_initiated: "Payment Initiated",
  payment_processed: "Payment Processed",
  payment_failed: "Payment Failed",
  payment_refunded: "Payment Refunded",
  payment_cancelled: "Payment Cancelled",
  payment_verified: "Payment Verified",

  // Equipment
  equipment_added: "Equipment Added",
  equipment_updated: "Equipment Updated",
  equipment_removed: "Equipment Removed",
  equipment_assigned: "Equipment Assigned",
  equipment_unassigned: "Equipment Unassigned",
  equipment_maintenance: "Equipment Maintenance",

  // Financial Year
  financial_year_created: "Financial Year Created",
  financial_year_updated: "Financial Year Updated",
  financial_year_deleted: "Financial Year Deleted",
  financial_year_activated: "Financial Year Activated",
  financial_year_deactivated: "Financial Year Deactivated",

  // Backup
  backup_created: "Backup Created",
  backup_deleted: "Backup Deleted",
  backup_restored: "Backup Restored",
  backup_scheduled: "Backup Scheduled",
  backup_failed: "Backup Failed",

  // Settings
  settings_updated: "Settings Updated",
  settings_reset: "Settings Reset",
  settings_imported: "Settings Imported",
  settings_exported: "Settings Exported",

  // Notification
  notification_sent: "Notification Sent",
  notification_failed: "Notification Failed",
  notification_template_created: "Notification Template Created",
  notification_template_updated: "Notification Template Updated",
  notification_template_deleted: "Notification Template Deleted",

  // Cleanup
  cleanup_executed: "Cleanup Executed",
  cleanup_scheduled: "Cleanup Scheduled",
  cleanup_failed: "Cleanup Failed",
  data_archived: "Data Archived",
  data_purged: "Data Purged",

  // System
  system_maintenance: "System Maintenance",
  error_occurred: "Error Occurred",
  system_startup: "System Startup",
  system_shutdown: "System Shutdown",
  configuration_changed: "Configuration Changed",
  security_event: "Security Event",
  performance_alert: "Performance Alert",

  // General
  entity_created: "Entity Created",
  entity_updated: "Entity Updated",
  entity_deleted: "Entity Deleted",
  batch_operation: "Batch Operation",
  import_operation: "Import Operation",
  export_operation: "Export Operation",
  validation_failed: "Validation Failed",
  authorization_failed: "Authorization Failed",
  other: "Other",

  // Webhook activity types (as stored by backend)
  SECURITY_EVENT: "Security Event",
  OTHER: "Other",
  PAYMENT_PROCESSED: "Payment Processed",
  PAYMENT_FAILED: "Payment Failed",
  BOOKING_PAYMENT_FAILED: "Booking Payment Failed",
  BOOKING_UPDATED: "Booking Updated",
};

const moduleLabels = {
  booking: "Booking",
  slot: "Slot",
  pricing: "Pricing",
  invoice: "Invoice",
  payment: "Payment",
  payment_polling: "Payment Polling",
  payment_webhook: "Payment Webhook",
  notification: "Notification",
  holiday: "Holiday",
  special_schedule: "Special Schedule",
  admin: "Admin",
  user: "User",
  system: "System",
  financial_year: "Financial Year",
  backup: "Backup",
  cleanup: "Cleanup",
  general: "General",
};

const UserIdTooltip = React.memo(({ userId }) => {
  const dispatch = useDispatch();
  const users = useSelector(selectUsers, shallowEqual);

  const [userDetails, setUserDetails] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  // request guards
  const requestedForRef = React.useRef(null);
  const isLoadingRef = React.useRef(false);
  React.useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const fetchUserDetails = React.useCallback(() => {
    const key = String(userId);
    if (requestedForRef.current === key) return;
    if (userDetails || isLoadingRef.current) return;

    requestedForRef.current = key;
    setIsLoading(true);
    dispatch(fetchUsersRequest({ search_term: key }));
  }, [dispatch, userId, userDetails]);

  // when users slice updates, try to resolve details
  React.useEffect(() => {
    if (!users || users.length === 0) return;
    if (!userId) return;
    const found = users.find((u) => u.id === userId);
    if (found) {
      setUserDetails(found);
      setIsLoading(false);
    }
  }, [users, userId]);

  const tooltipTitle = React.useMemo(() => {
    if (isLoading) return "Loading user details...";
    if (userDetails) {
      return `${userDetails.first_name || ""} ${
        userDetails.last_name || ""
      } - ${userDetails.mobile_number || ""}`;
    }
    return `User ID: ${userId}`;
  }, [isLoading, userDetails, userId]);

  return (
    <Tooltip
      title={tooltipTitle}
      placement="top"
      onOpen={fetchUserDetails}
      disableTouchListener
      disableFocusListener
    >
      <Typography
        variant="body2"
        fontFamily="monospace"
        style={{ cursor: "help" }}
      >
        {userId}
      </Typography>
    </Tooltip>
  );
});
UserIdTooltip.displayName = "UserIdTooltip";

const MetadataFieldTooltip = React.memo(
  ({ fieldName, fieldValue, fieldKey }) => {
    const dispatch = useDispatch();
    const users = useSelector(selectUsers, shallowEqual);

    const [userDetails, setUserDetails] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);

    const shouldShowUserDetails = React.useMemo(
      () =>
        fieldName.toLowerCase().includes("user") ||
        fieldName.toLowerCase().includes("created") ||
        fieldName.toLowerCase().includes("updated"),
      [fieldName]
    );

    const requestedForRef = React.useRef(null);
    const isLoadingRef = React.useRef(false);
    React.useEffect(() => {
      isLoadingRef.current = isLoading;
    }, [isLoading]);

    const fetchUserDetails = React.useCallback(() => {
      if (!shouldShowUserDetails) return;
      const key = String(fieldValue);
      if (requestedForRef.current === key) return;
      if (userDetails || isLoadingRef.current) return;

      requestedForRef.current = key;
      setIsLoading(true);
      dispatch(fetchUsersRequest({ search_term: key }));
    }, [dispatch, shouldShowUserDetails, fieldValue, userDetails]);

    React.useEffect(() => {
      if (!shouldShowUserDetails) return;
      if (!users || users.length === 0) return;
      const found = users.find((u) => u.id === fieldValue);
      if (found) {
        setUserDetails(found);
        setIsLoading(false);
      }
    }, [users, fieldValue, shouldShowUserDetails]);

    const tooltipTitle = React.useMemo(() => {
      if (!shouldShowUserDetails) return `${fieldName}: ${fieldValue}`;
      if (isLoading) return "Loading user details...";
      if (userDetails) {
        return `${userDetails.first_name || ""} ${
          userDetails.last_name || ""
        } - ${userDetails.mobile_number || ""}`;
      }
      return `${fieldName}: ${fieldValue}`;
    }, [shouldShowUserDetails, fieldName, fieldValue, isLoading, userDetails]);

    // special display for changed_slots
    if (fieldKey === "changed_slots" && Array.isArray(fieldValue)) {
      return (
        <Box
          sx={{
            backgroundColor: "grey.100",
            p: 0.5,
            borderRadius: 0.5,
            border: "1px solid",
            borderColor: "grey.300",
            maxHeight: 100,
            overflowY: "auto",
            wordBreak: "break-word",
          }}
        >
          {fieldValue.length > 0 ? (
            fieldValue.map((change, idx) => (
              <Typography
                key={idx}
                variant="body2"
                fontFamily="monospace"
                sx={{
                  color:
                    change.to === "available" ? "success.dark" : "error.dark",
                  fontSize: "0.75rem",
                }}
              >
                {change.time}: {change.from} → {change.to}
              </Typography>
            ))
          ) : (
            <Typography
              variant="body2"
              fontFamily="monospace"
              color="textSecondary"
            >
              No specific slot changes recorded.
            </Typography>
          )}
        </Box>
      );
    }

    if (shouldShowUserDetails) {
      return (
        <Tooltip
          title={tooltipTitle}
          placement="top"
          onOpen={fetchUserDetails}
          disableTouchListener
          disableFocusListener
        >
          <Typography
            variant="body2"
            fontFamily="monospace"
            sx={{
              wordBreak: "break-word",
              backgroundColor: "grey.100",
              p: 0.5,
              borderRadius: 0.5,
              border: "1px solid",
              borderColor: "grey.300",
              cursor: "help",
            }}
          >
            {fieldValue}
          </Typography>
        </Tooltip>
      );
    }

    return (
      <Typography
        variant="body2"
        fontFamily="monospace"
        sx={{
          wordBreak: "break-word",
          backgroundColor: "grey.100",
          p: 0.5,
          borderRadius: 0.5,
          border: "1px solid",
          borderColor: "grey.300",
        }}
      >
        {fieldValue}
      </Typography>
    );
  }
);
MetadataFieldTooltip.displayName = "MetadataFieldTooltip";

/** ---------- Helpers ---------- */

const gmtToIST = (gmtString) => {
  try {
    const utcDate = new Date(gmtString);
    const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);
    const day = String(istDate.getDate()).padStart(2, "0");
    const month = String(istDate.getMonth() + 1).padStart(2, "0");
    const year = istDate.getFullYear();
    let hours = istDate.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutes = String(istDate.getMinutes()).padStart(2, "0");
    const seconds = String(istDate.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
  } catch {
    return gmtString;
  }
};

const WEBHOOK_ACTION_PREFIX = "webhook_";
const webhookSuccessActions = new Set(["webhook_processed"]);
const webhookFailureActions = new Set([
  "webhook_process_failed",
  "signature_rejected",
  "webhook_no_booking",
  "no_booking_found",
]);
const webhookSkippedActions = new Set([
  "webhook_skipped",
  "webhook_skipped_already_processed",
  "skipped",
]);
const webhookKnownActions = new Set([
  ...webhookSuccessActions,
  ...webhookFailureActions,
  ...webhookSkippedActions,
]);

const webhookActionOptions = [
  { value: "webhook_processed", label: "Webhook Processed" },
  { value: "webhook_process_failed", label: "Webhook Process Failed" },
  { value: "signature_rejected", label: "Signature Rejected" },
  { value: "webhook_no_booking", label: "No Booking Found" },
  { value: "no_booking_found", label: "No Booking Found (Alt)" },
  { value: "webhook_skipped", label: "Webhook Skipped" },
  {
    value: "webhook_skipped_already_processed",
    label: "Skipped (Already Processed)",
  },
  { value: "skipped", label: "Skipped (Alt)" },
];

const isWebhookEvent = (log) => {
  if (!log) return false;
  const action = log.action || "";
  return (
    log.module === "payment_webhook" ||
    action.startsWith(WEBHOOK_ACTION_PREFIX) ||
    webhookKnownActions.has(action)
  );
};

const getWebhookStatusChip = (log) => {
  const action = log?.action || "";
  if (webhookSuccessActions.has(action)) {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="Processed"
        color="success"
        size="small"
      />
    );
  }
  if (webhookSkippedActions.has(action)) {
    return (
      <Chip icon={<InfoIcon />} label="Skipped" color="warning" size="small" />
    );
  }
  if (webhookFailureActions.has(action) || action === "signature_rejected") {
    return (
      <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
    );
  }
  if (log?.is_successful) {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="Success"
        color="success"
        size="small"
      />
    );
  }
  return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />;
};

const getStatusChip = (log) => {
  if (isWebhookEvent(log)) {
    return getWebhookStatusChip(log);
  }
  if (log.activity_type === "error_occurred") {
    return (
      <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" />
    );
  }
  if (log.is_successful) {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="Success"
        color="success"
        size="small"
      />
    );
  }
  return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />;
};

const getWebhookMetaSummary = (log) => {
  if (!isWebhookEvent(log)) return null;
  const metadata = log.metadata || {};
  const items = [];
  if (metadata.payment_id) items.push(`payment: ${metadata.payment_id}`);
  if (metadata.order_id) items.push(`order: ${metadata.order_id}`);
  if (metadata.booking_id) items.push(`booking: ${metadata.booking_id}`);
  if (metadata.status) items.push(`status: ${metadata.status}`);
  if (metadata.current_status)
    items.push(`current: ${metadata.current_status}`);
  if (metadata.error) items.push(`error: ${metadata.error}`);
  return items.length > 0 ? items.join(" • ") : null;
};

/** ---------- Row (memoized) ---------- */

const Row = React.memo(function Row({ log, index, expanded, onToggle }) {
  const hasSlotSummary =
    log.activity_type === "slot_updated" &&
    log.metadata &&
    log.metadata.changes_summary;

  const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;
  const hasError = Boolean(log.error_message);
  const webhookSummary = getWebhookMetaSummary(log);

  return (
    <>
      <TableRow
        hover
        onClick={() => onToggle(log.id || index)}
        sx={{
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
          backgroundColor: expanded ? "action.selected" : "inherit",
        }}
      >
        <TableCell>{gmtToIST(log.created_at)}</TableCell>
        <TableCell>
          <Chip
            label={activityTypeLabels[log.activity_type] || log.activity_type}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          {log.location ? (
            <Chip label={log.location} size="small" />
          ) : (
            <Typography variant="body2" color="textSecondary">
              -
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {log.sport ? (
            <Chip
              label={sportMappingLabels[log.sport]}
              size="small"
              variant="outlined"
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              -
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Tooltip title={log.description} placement="top">
            <Box>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {log.description}
              </Typography>
              {webhookSummary && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    color: "text.secondary",
                  }}
                >
                  {webhookSummary}
                </Typography>
              )}
              {hasSlotSummary && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color:
                      log.metadata.baseline_source === "existing_database"
                        ? "success.main"
                        : "info.main",
                    fontWeight: "bold",
                    backgroundColor:
                      log.metadata.baseline_source === "existing_database"
                        ? "#e8f5e8"
                        : "#e3f2fd",
                    px: 1,
                    py: 0.5,
                    borderRadius: 0.5,
                    mt: 0.5,
                    fontSize: "0.7rem",
                    cursor: "default",
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                >
                  {log.metadata.baseline_source === "existing_database"
                    ? "🎯"
                    : "🆕"}{" "}
                  {log.metadata.changes_summary.split(":")[1]?.trim() ||
                    log.metadata.changes_summary}
                </Typography>
              )}
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell>
          {log.module ? (
            <Chip
              label={moduleLabels[log.module] || log.module}
              size="small"
              color="primary"
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              -
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {log.user_id ? (
            <UserIdTooltip userId={log.user_id} />
          ) : (
            <Typography variant="body2" color="textSecondary">
              -
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {getStatusChip(log)}
        </TableCell>
        <TableCell>
          <Box>
            {hasError && (
              <Tooltip title={log.error_message} placement="top">
                <Typography variant="body2" color="error" fontSize="0.75rem">
                  Error: {log.error_message.substring(0, 50)}...
                </Typography>
              </Tooltip>
            )}
            {hasMetadata && (
              <Typography
                variant="body2"
                color="textSecondary"
                fontSize="0.75rem"
              >
                +{Object.keys(log.metadata).length} metadata fields
                <br />
                <span style={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                  {Object.keys(log.metadata).slice(0, 3).join(", ")}
                  {Object.keys(log.metadata).length > 3 && "..."}
                </span>
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.5,
                color: "primary.main",
              }}
            >
              {expanded ? (
                <>
                  <ExpandLessIcon fontSize="small" />
                  <Typography variant="body2" fontSize="0.75rem">
                    Click to collapse
                  </Typography>
                </>
              ) : (
                <>
                  <ExpandMoreIcon fontSize="small" />
                  <Typography variant="body2" fontSize="0.75rem">
                    Click to expand
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={9} style={{ padding: 0 }}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "grey.50",
                borderLeft: "4px solid",
                borderLeftColor: "primary.main",
                mx: 1,
                my: 0.5,
                borderRadius: 1,
              }}
            >
              <Grid container spacing={2}>
                {/* Basic Details */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Activity Details
                  </Typography>
                  <Box className={styles.marginBottom2}>
                    <Typography
                      variant="body2"
                      className={styles.marginBottom1}
                    >
                      <strong>Resource:</strong> {log.resource || "N/A"}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.marginBottom1}
                    >
                      <strong>Action:</strong> {log.action || "N/A"}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.marginBottom1}
                    >
                      <strong>Execution Time:</strong>{" "}
                      {log.execution_time_ms || 0}ms
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.marginBottom1}
                    >
                      <strong>IP Address:</strong> {log.ip_address || "N/A"}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.marginBottom1}
                    >
                      <strong>Session ID:</strong> {log.session_id || "N/A"}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.marginBottom1}
                    >
                      <strong>User Agent:</strong> {log.user_agent || "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Error Message */}
                {log.error_message ? (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Error Details
                    </Typography>
                    <Alert severity="error" className={styles.marginBottom2}>
                      {log.error_message}
                    </Alert>
                    {log.metadata && log.metadata.error_stack && (
                      <Box className={styles.marginBottom2}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Error Stack
                        </Typography>
                        <Paper variant="outlined" className={styles.padding1}>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontSize: "0.75rem",
                            }}
                          >
                            {log.metadata.error_stack}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Grid>
                ) : (
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="subtitle2"
                      color="success.main"
                      gutterBottom
                    >
                      No Errors
                    </Typography>
                    <Alert severity="success" className={styles.marginBottom2}>
                      This activity completed successfully without any errors.
                    </Alert>
                  </Grid>
                )}

                {/* Slot Update Summary */}
                {(log.activity_type === "slot_blocked" ||
                  log.activity_type === "slot_unblocked") &&
                  log.metadata && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        color="success"
                        gutterBottom
                      >
                        🎯{" "}
                        {log.metadata.baseline_source === "existing_database"
                          ? "Slot Update Summary"
                          : "Slot Configuration Summary"}
                      </Typography>
                      <Paper
                        variant="outlined"
                        className={`${styles.slotMetadataPaper} ${
                          log.metadata.baseline_source === "existing_database"
                            ? styles.slotMetadataPaperExisting
                            : styles.slotMetadataPaperDefault
                        }`}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box
                              className={`${styles.textAlignCenter} ${styles.padding1}`}
                            >
                              <Typography variant="h6" color="success.main">
                                {log.metadata.baseline_available_count || 0}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {log.metadata.baseline_source ===
                                "existing_database"
                                  ? "Previous Available"
                                  : "Default Available"}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box
                              className={`${styles.textAlignCenter} ${styles.padding1}`}
                            >
                              <Typography variant="h6" color="primary.main">
                                {log.metadata.new_available_count || 0}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                New Available
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box
                              className={`${styles.textAlignCenter} ${styles.padding1}`}
                            >
                              <Typography variant="h6" color="warning.main">
                                {log.metadata.changed_slots_count || 0}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Slots Changed
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box
                              className={`${styles.textAlignCenter} ${styles.padding1}`}
                            >
                              <Typography variant="h6" color="info.main">
                                {log.metadata.total_slots || 0}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Total Slots
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box className={styles.baselineInfoBox}>
                              <Typography
                                variant="caption"
                                className={styles.baselineInfoText}
                              >
                                📋 Baseline:{" "}
                                {log.metadata.baseline_source ===
                                "existing_database"
                                  ? "Compared with existing database slots"
                                  : "Compared with default available state"}
                              </Typography>
                            </Box>
                          </Grid>

                          {log.metadata.changes_summary && (
                            <Grid item xs={12}>
                              <Box
                                className={`${styles.changesSummaryBox} ${
                                  log.metadata.baseline_source ===
                                  "existing_database"
                                    ? styles.changesSummaryBoxExisting
                                    : styles.changesSummaryBoxDefault
                                }`}
                              >
                                <Typography
                                  variant="body2"
                                  className={
                                    log.metadata.baseline_source ===
                                    "existing_database"
                                      ? styles.changesSummaryTextExisting
                                      : styles.changesSummaryTextDefault
                                  }
                                  fontWeight="bold"
                                >
                                  📊 {log.metadata.changes_summary}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>
                  )}

                {/* Webhook Summary */}
                {isWebhookEvent(log) && (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      gutterBottom
                    >
                      Webhook Details
                    </Typography>
                    <Paper
                      variant="outlined"
                      className={`${styles.padding2} ${styles.backgroundColorWhite}`}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="caption" color="textSecondary">
                            Action
                          </Typography>
                          <Typography variant="body2">
                            {log.action || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="caption" color="textSecondary">
                            Operation
                          </Typography>
                          <Typography variant="body2">
                            {log.metadata?.operation || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="caption" color="textSecondary">
                            Payment ID
                          </Typography>
                          <Typography variant="body2">
                            {log.metadata?.payment_id || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="caption" color="textSecondary">
                            Order ID
                          </Typography>
                          <Typography variant="body2">
                            {log.metadata?.order_id || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="caption" color="textSecondary">
                            Booking ID
                          </Typography>
                          <Typography variant="body2">
                            {log.metadata?.booking_id || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="caption" color="textSecondary">
                            Status
                          </Typography>
                          <Typography variant="body2">
                            {log.metadata?.status ||
                              log.metadata?.current_status ||
                              "N/A"}
                          </Typography>
                        </Grid>
                        {log.metadata?.error && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">
                              Error
                            </Typography>
                            <Typography variant="body2" color="error">
                              {log.metadata.error}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {/* Metadata */}
                {hasMetadata ? (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      gutterBottom
                    >
                      Metadata
                    </Typography>
                    <Paper
                      variant="outlined"
                      className={`${styles.padding2} ${styles.backgroundColorWhite}`}
                    >
                      <Grid container spacing={1}>
                        {log.metadata && log.metadata.payload && (
                          <Grid item xs={12}>
                            <Typography
                              variant="subtitle2"
                              color="primary"
                              gutterBottom
                            >
                              Request Payload
                            </Typography>
                            <Paper
                              variant="outlined"
                              className={styles.padding1}
                            >
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                                sx={{ fontSize: "0.8rem" }}
                              >
                                {(() => {
                                  try {
                                    return JSON.stringify(
                                      log.metadata.payload,
                                      null,
                                      2
                                    );
                                  } catch {
                                    return String(log.metadata.payload);
                                  }
                                })()}
                              </Typography>
                            </Paper>
                          </Grid>
                        )}
                        {Object.entries(log.metadata)
                          .filter(
                            ([k]) => k !== "payload" && k !== "error_stack"
                          )
                          .map(([key, value]) => (
                            <Grid item xs={12} sm={6} md={4} key={key}>
                              <Box className={styles.marginBottom1}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  display="block"
                                >
                                  {key
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </Typography>
                                <MetadataFieldTooltip
                                  fieldName={key
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                  fieldValue={
                                    typeof value === "object"
                                      ? JSON.stringify(value, null, 2)
                                      : String(value)
                                  }
                                  fieldKey={key}
                                />
                              </Box>
                            </Grid>
                          ))}
                      </Grid>
                    </Paper>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      No Metadata
                    </Typography>
                    <Alert severity="info" className={styles.marginBottom2}>
                      This activity doesn't have any additional metadata fields.
                    </Alert>
                  </Grid>
                )}

                {/* Timestamps */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Timestamps
                  </Typography>
                  <Box
                    className={`${styles.displayFlex} ${styles.gap2} ${styles.flexWrapWrap}`}
                  >
                    <Typography variant="body2">
                      <strong>Created:</strong> {gmtToIST(log.created_at)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Updated:</strong> {gmtToIST(log.updated_at)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
});

/** ---------- Main Component ---------- */

const ActivityLog = React.memo(function ActivityLog({
  location,
  sport = null,
  defaultView = "all",
  forceModule = "",
  hideSummary = false,
  hideViewToggle = false,
  hideModuleFilter = false,
}) {
  const dispatch = useDispatch();
  const { logs, summary, total, loading, error, filters } = useSelector(
    (state) => state.activityLog
  );

  const [showFilters, setShowFilters] = React.useState(false);
  const PAGE_STORAGE_KEY = "activity_log_page";
  const [page, setPage] = React.useState(() => {
    try {
      const saved = localStorage.getItem(PAGE_STORAGE_KEY);
      const parsed = Number(saved);
      return parsed;
    } catch {}
    return 0;
  });
  const ROWS_PER_PAGE_STORAGE_KEY = "activity_log_rows_per_page";
  const [rowsPerPage, setRowsPerPage] = React.useState(() => {
    try {
      const saved = localStorage.getItem(ROWS_PER_PAGE_STORAGE_KEY);
      const parsed = Number(saved);
      if (parsed > 0) return parsed;
      return 25;
    } catch {}
    return 25;
  });
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [expandedRows, setExpandedRows] = React.useState(new Set());
  const [activeView, setActiveView] = React.useState(defaultView);
  const [webhookActionFilter, setWebhookActionFilter] = React.useState("");
  const moduleSetByViewRef = React.useRef(false);

  // UNCONTROLLED input refs (typing doesn't re-render component)
  const userIdRef = React.useRef(null);
  const relatedEntityTypeRef = React.useRef(null);
  const relatedEntityIdRef = React.useRef(null);

  // primitives from redux; ONLY use these in effect deps
  const startStr = filters.start_date;
  const endStr = filters.end_date;
  const hasDates = Boolean(startStr && endStr);

  // UI-only dayjs values
  const currentStartDate = React.useMemo(
    () => (startStr ? dayjs(startStr) : null),
    [startStr]
  );
  const currentEndDate = React.useMemo(
    () => (endStr ? dayjs(endStr) : null),
    [endStr]
  );

  // Refs for stable reads inside callbacks/effects
  const currentStartDateRef = React.useRef(null);
  const currentEndDateRef = React.useRef(null);
  const filtersRef = React.useRef(filters);
  const pageRef = React.useRef(page);
  const rowsPerPageRef = React.useRef(rowsPerPage);

  React.useEffect(() => {
    currentStartDateRef.current = filters.start_date;
    currentEndDateRef.current = filters.end_date;
    filtersRef.current = filters;
    pageRef.current = page;
    rowsPerPageRef.current = rowsPerPage;
  }, [filters, page, rowsPerPage]);

  React.useEffect(() => {
    setPage(0);
    localStorage.setItem(PAGE_STORAGE_KEY, "0");
  }, [activeView]);

  React.useEffect(() => {
    if (activeView !== "webhook" && webhookActionFilter) {
      setWebhookActionFilter("");
    }
  }, [activeView, webhookActionFilter]);

  React.useEffect(() => {
    if (forceModule) {
      dispatch(setActivityLogsFilters({ module: forceModule }));
      return;
    }
    if (activeView === "webhook") {
      if (filtersRef.current.module !== "payment_webhook") {
        dispatch(setActivityLogsFilters({ module: "payment_webhook" }));
        moduleSetByViewRef.current = true;
      }
    } else if (moduleSetByViewRef.current) {
      dispatch(setActivityLogsFilters({ module: "" }));
      moduleSetByViewRef.current = false;
    }
  }, [activeView, forceModule, dispatch]);

  React.useEffect(() => {
    if (!hasDates) {
      dispatch(
        setActivityLogsFilters({
          start_date: dayjs().subtract(1, "day").toISOString(),
          end_date: dayjs().add(1, "day").toISOString(),
        })
      );
      return;
    }
    if (!isInitialized) setIsInitialized(true);
  }, [hasDates, isInitialized, dispatch]);

  // fetch data once initialized and when deps change
  React.useEffect(() => {
    if (!isInitialized || !hasDates) return;

    const startDate = currentStartDateRef.current;
    const endDate = currentEndDateRef.current;
    const f = filtersRef.current;

    // read current values from refs (no re-render while typing)
    const user_id = userIdRef.current?.value?.trim() || undefined;
    const related_entity_type =
      relatedEntityTypeRef.current?.value?.trim() || undefined;
    const related_entity_id =
      relatedEntityIdRef.current?.value?.trim() || undefined;

    dispatch(
      fetchActivityLogsRequest({
        filters: {
          ...f,
          start_date: startDate,
          end_date: endDate,
          user_id,
          related_entity_type,
          related_entity_id,
        },
        pagination: {
          page: pageRef.current,
          rowsPerPage: rowsPerPageRef.current,
        },
        location,
        sport,
      })
    );

    if (!hideSummary) {
      dispatch(fetchActivitySummaryRequest({ location, sport }));
    }
  }, [
    isInitialized,
    hasDates,
    location,
    sport,
    page,
    rowsPerPage,
    // only primitive filter deps:
    startStr,
    endStr,
    filters.activity_type,
    filters.module,
    filters.is_successful,
    filters.user_id,
    filters.related_entity_type,
    filters.related_entity_id,
    hideSummary,
    dispatch,
  ]);

  const handlePageChange = React.useCallback((_, newPage) => {
    setPage(newPage);
    localStorage.setItem(PAGE_STORAGE_KEY, String(newPage));
  }, []);
  const handleRowsPerPageChange = React.useCallback((e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    localStorage.setItem(ROWS_PER_PAGE_STORAGE_KEY, String(e.target.value));
    setPage(Number(localStorage.getItem(PAGE_STORAGE_KEY)));
  }, []);

  const handleRowExpand = React.useCallback((logId) => {
    setExpandedRows((prev) => {
      const s = new Set(prev);
      if (s.has(logId)) s.delete(logId);
      else s.add(logId);
      return s;
    });
  }, []);

  const handleFilterChange = React.useCallback(
    (field, value) => {
      if (field !== "page" && field !== "rowsPerPage")
        setPage(Number(localStorage.getItem(PAGE_STORAGE_KEY)));
      let serializedValue = value;
      if (
        (field === "start_date" || field === "end_date") &&
        value &&
        value.isValid &&
        value.isValid()
      ) {
        serializedValue = value.toISOString();
      }
      dispatch(setActivityLogsFilters({ [field]: serializedValue }));
    },
    [dispatch]
  );

  const clearFilters = React.useCallback(() => {
    setPage(Number(localStorage.getItem(PAGE_STORAGE_KEY)));

    // clear uncontrolled inputs WITHOUT re-render
    if (userIdRef.current) userIdRef.current.value = "";
    if (relatedEntityTypeRef.current) relatedEntityTypeRef.current.value = "";
    if (relatedEntityIdRef.current) relatedEntityIdRef.current.value = "";

    dispatch(resetActivityLogsFilters());
    dispatch(
      setActivityLogsFilters({
        start_date: dayjs().subtract(1, "day").toISOString(),
        end_date: dayjs().add(1, "day").toISOString(),
      })
    );
  }, [dispatch]);

  const refreshData = React.useCallback(() => {
    const startDate = currentStartDateRef.current;
    const endDate = currentEndDateRef.current;
    const f = filtersRef.current;

    const user_id = userIdRef.current?.value?.trim() || undefined;
    const related_entity_type =
      relatedEntityTypeRef.current?.value?.trim() || undefined;
    const related_entity_id =
      relatedEntityIdRef.current?.value?.trim() || undefined;

    dispatch(
      fetchActivityLogsRequest({
        filters: {
          ...f,
          start_date: startDate,
          end_date: endDate,
          user_id,
          related_entity_type,
          related_entity_id,
        },
        pagination: {
          page: pageRef.current,
          rowsPerPage: rowsPerPageRef.current,
        },
        location,
        sport,
      })
    );
  }, [dispatch, location, sport]);

  const refreshSummary = React.useCallback(() => {
    if (hideSummary) return;
    dispatch(fetchActivitySummaryRequest({ location, sport }));
  }, [dispatch, location, sport, hideSummary]);

  const visibleLogs = React.useMemo(() => {
    if (activeView !== "webhook") return logs;
    const webhookLogs = logs.filter((log) => isWebhookEvent(log));
    if (!webhookActionFilter) return webhookLogs;
    return webhookLogs.filter(
      (log) => (log.action || "") === webhookActionFilter
    );
  }, [logs, activeView, webhookActionFilter]);

  const hasCriticalError = Boolean(error.logs) && logs.length === 0;

  const renderCriticalError = React.useCallback(
    () => (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <ErrorIcon color="error" className={styles.iconLarge} />
        <Typography variant="h6" color="error">
          Failed to load activity logs
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          {error.logs}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            dispatch(
              setActivityLogsFilters({
                start_date: dayjs().subtract(1, "day").toISOString(),
                end_date: dayjs().add(1, "day").toISOString(),
              })
            );
          }}
        >
          Retry
        </Button>
      </Box>
    ),
    [dispatch, error.logs]
  );

  const renderLoading = React.useCallback(
    () => (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Loading activity logs...
        </Typography>
      </Box>
    ),
    []
  );

  const renderSettingUpDates = React.useCallback(
    () => (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <InfoIcon color="info" className={styles.iconLarge} />
        <Typography variant="h6" color="info">
          Setting up date filters...
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          Please wait while we initialize the date range for activity logs.
        </Typography>
        <CircularProgress size={24} />
      </Box>
    ),
    []
  );

  // Memoize the table so other UI changes don’t repaint it
  const tableView = React.useMemo(
    () => (
      <Paper className={styles.tableSection}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Activity Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Sport</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="textSecondary">
                      No activity logs found for the selected view.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleLogs.map((log, index) => (
                  <Row
                    key={log.id || index}
                    log={log}
                    index={index}
                    expanded={expandedRows.has(log.id || index)}
                    onToggle={handleRowExpand}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={activeView === "webhook" ? visibleLogs.length : total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    ),
    [
      visibleLogs,
      total,
      page,
      rowsPerPage,
      handlePageChange,
      handleRowsPerPageChange,
      expandedRows,
      activeView,
    ]
  );

  // Early returns
  if (hasCriticalError) return renderCriticalError();
  if (loading.logs && logs.length === 0) return renderLoading();
  if (!hasDates) return renderSettingUpDates();

  return (
    <Box className={styles.container}>
      <Typography variant="h4" gutterBottom className={styles.title}>
        Activity Logs
      </Typography>

      {/* Summary */}
      {summary && !hideSummary && (
        <Grid container spacing={3} className={styles.summarySection}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.summaryCard}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Activities
                </Typography>
                <Typography variant="h4" component="div">
                  {summary.total_activities}
                </Typography>
                <Chip
                  icon={<TrendingUpIcon />}
                  label="Last 30 days"
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.summaryCard}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Successful
                </Typography>
                <Typography variant="h4" component="div" color="success.main">
                  {summary.successful_activities}
                </Typography>
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Success"
                  size="small"
                  color="success"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.summaryCard}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Failed
                </Typography>
                <Typography variant="h4" component="div" color="error.main">
                  {summary.failed_activities}
                </Typography>
                <Chip
                  icon={<ErrorIcon />}
                  label="Failed"
                  size="small"
                  color="error"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.summaryCard}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Top Module
                </Typography>
                <Typography variant="h6" component="div">
                  {Object.keys(summary.activities_by_module).length > 0
                    ? Object.entries(summary.activities_by_module).sort(
                        ([, a], [, b]) => b - a
                      )[0][0]
                    : "N/A"}
                </Typography>
                <Chip icon={<InfoIcon />} label="Most Active" size="small" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper className={styles.filtersSection}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Filters</Typography>
          <Box>
            {!hideViewToggle && (
              <>
                <Button
                  variant={activeView === "all" ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setActiveView("all")}
                  className={styles.marginRight1}
                >
                  All Activity
                </Button>
                <Button
                  variant={activeView === "webhook" ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setActiveView("webhook")}
                  className={styles.marginRight1}
                >
                  Webhook Events
                </Button>
              </>
            )}
            <Button
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters((v) => !v)}
              variant="outlined"
              size="small"
              className={styles.marginRight1}
            >
              {showFilters ? "Hide" : "Show"} Filters
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={refreshData}
              variant="outlined"
              size="small"
              className={styles.marginRight1}
            >
              Refresh Logs
            </Button>
            {!hideSummary && (
              <Button
                startIcon={<RefreshIcon />}
                onClick={refreshSummary}
                variant="outlined"
                size="small"
                className={styles.marginRight1}
              >
                Refresh Summary
              </Button>
            )}
            <Button
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              variant="outlined"
              size="small"
              color="secondary"
              className={styles.marginRight1}
            >
              Clear
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={refreshData}
              variant="contained"
              size="small"
              className={styles.refreshButton}
            >
              Refresh Data
            </Button>
          </Box>
        </Box>

        {showFilters && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={filters.activity_type || ""}
                  onChange={(e) =>
                    handleFilterChange("activity_type", e.target.value)
                  }
                  label="Activity Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {Object.entries(activityTypeLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {!hideModuleFilter && !forceModule && activeView !== "webhook" && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Module</InputLabel>
                  <Select
                    value={filters.module || ""}
                    onChange={(e) =>
                      handleFilterChange("module", e.target.value)
                    }
                    label="Module"
                  >
                    <MenuItem value="">All Modules</MenuItem>
                    {Object.entries(moduleLabels).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {activeView === "webhook" && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Webhook Action</InputLabel>
                  <Select
                    value={webhookActionFilter}
                    onChange={(e) => setWebhookActionFilter(e.target.value)}
                    label="Webhook Action"
                  >
                    <MenuItem value="">All Actions</MenuItem>
                    {webhookActionOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.is_successful || ""}
                  onChange={(e) =>
                    handleFilterChange("is_successful", e.target.value)
                  }
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Successful</MenuItem>
                  <MenuItem value="false">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Uncontrolled text inputs — keep focus and no re-render while typing */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="User ID"
                inputRef={userIdRef}
                defaultValue=""
                placeholder="Enter user ID"
                onKeyDown={(e) => {
                  if (e.key === "Enter") refreshData();
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={currentStartDate || dayjs().subtract(7, "day")}
                  onChange={(date) => handleFilterChange("start_date", date)}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") refreshData();
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={currentEndDate || dayjs().add(1, "day")}
                  onChange={(date) => handleFilterChange("end_date", date)}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") refreshData();
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Entity Type"
                inputRef={relatedEntityTypeRef}
                defaultValue=""
                placeholder="e.g., booking, user"
                onKeyDown={(e) => {
                  if (e.key === "Enter") refreshData();
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Entity ID"
                inputRef={relatedEntityIdRef}
                defaultValue=""
                placeholder="Enter entity ID"
                onKeyDown={(e) => {
                  if (e.key === "Enter") refreshData();
                }}
              />
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Error Alert */}
      {error.logs && logs.length > 0 && (
        <Alert severity="error" className={styles.errorAlert}>
          {error.logs}
        </Alert>
      )}

      {/* Loading indicator for subsequent requests */}
      {loading.logs && logs.length > 0 && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Table */}
      {tableView}
    </Box>
  );
});
ActivityLog.displayName = "ActivityLog";

export default ActivityLog;
